import { useState, useEffect, useCallback } from 'react';
import { apiRequest } from '../config/api';
import { useAuth } from '../context/AuthContext';

// Module-level cache so multiple components share the same data
let cachedDefs = null;
let cacheOrgId = null;

export default function useObjectDefinitions() {
  const { isAuthenticated, organization } = useAuth();
  const [objectDefinitions, setObjectDefinitions] = useState(
    (organization && cacheOrgId === organization?.id) ? cachedDefs || [] : []
  );
  const [loading, setLoading] = useState(!cachedDefs || cacheOrgId !== organization?.id);

  const fetchObjectDefinitions = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiRequest('/crm/objects/');
      if (res.ok) {
        const data = await res.json();
        const defs = Array.isArray(data) ? data : [];
        cachedDefs = defs;
        cacheOrgId = organization?.id;
        setObjectDefinitions(defs);
      }
    } catch (e) {
      console.error('Error fetching object definitions:', e);
    } finally {
      setLoading(false);
    }
  }, [organization?.id]);

  useEffect(() => {
    if (!isAuthenticated || !organization) return;
    if (cachedDefs && cacheOrgId === organization.id) {
      setObjectDefinitions(cachedDefs);
      setLoading(false);
      return;
    }
    fetchObjectDefinitions();
  }, [isAuthenticated, organization, fetchObjectDefinitions]);

  const getBySlug = useCallback(
    (slug) => objectDefinitions.find((od) => od.slug === slug) || null,
    [objectDefinitions]
  );

  const getBySlugPlural = useCallback(
    (slugPlural) => objectDefinitions.find((od) => od.slug_plural === slugPlural || od.slug === slugPlural) || null,
    [objectDefinitions]
  );

  const getById = useCallback(
    (id) => objectDefinitions.find((od) => od.id === id) || null,
    [objectDefinitions]
  );

  /** Get the URL path for an object definition: /data/{slug_plural || slug} */
  const getObjectPath = useCallback(
    (od) => `/data/${od.slug_plural || od.slug}`,
    []
  );

  /** Get the URL path for a record: /data/{slug_plural || slug}/{recordId} */
  const getRecordPath = useCallback(
    (od, recordId) => `/data/${od.slug_plural || od.slug}/${recordId}`,
    []
  );

  return { objectDefinitions, loading, fetchObjectDefinitions, getBySlug, getBySlugPlural, getById, getObjectPath, getRecordPath };
}
